/**
 * THE ACTIVITY FEED, ON THE GLOBAL FILTER.
 *
 * The rows, the chips and the copy are the desk feed's — the four stat tiles,
 * the group tabs, the per-row tags, the desk-vs-sweep distinction on a
 * departure, "times in <tz>" in the footer. What changes is where the state
 * lives: the feed used to hold its own filters, its own search and its own page,
 * so a saved view could not restore it and a filter set on the top bar did not
 * reach it.
 *
 * Here the feed reads the SAME filter object as every chart, which is why a
 * chart click can land on it and why the whole thing survives a reload.
 *
 * ── THE TILES ARE CONTROLS ──
 * Owner report on the desk feed, 2026-08-14: "these are not respected as
 * filters". They were divs — four numbers describing a narrowing the reader then
 * had to perform by hand, on a panel whose whole argument is that the answer is
 * already on screen. They are buttons, they are `aria-pressed`, and the pressed
 * state is LOUD, because a feed narrowed to one direction looks exactly like a
 * quiet day.
 *
 * ── ONE TIMELINE, NOT FOUR LISTS ──
 * Everything in one stream, newest first, and the FILTERS are what produce a
 * group. The sequence is the thing a desk reads a feed for ("the parcel came in,
 * then the guest arrived"), and a grouped view makes the reader assemble it out
 * of four separate lists.
 */
import React from 'react';
import styled from '@emotion/styled';
import {
  ActivityEntry,
  ActivityFeedResponse,
} from '../../../../../api/activity-client';
import { normalizeUserRole, roleLabel } from '../../../../../auth/roles';
import {
  openRecordLabel,
  sourceNoun,
} from '../../../../../function-utils/record-label';
import {
  palette,
  radius,
  spacing,
  /* Aliased: this module already has a local `status` (an entry's severity
     string), and the token bundle would shadow it. */
  status as statusTokens,
  transition,
  typography,
} from '../../../../../style-utils/theme';
import { down, touch } from '../../../../../style-utils/media-queries';
import { PanelShell, ChartShellState } from '../charts/chart-shell';
import { PanelToggle } from '../charts/chart.styled';
import { DashboardFilter } from '../state/filter-model';
import { FilterAction } from '../state/filter-reducer';
import { useClock } from '../../../../../context/clock-context';
import { ClockNote } from '../../../../clock-note/clock-note';
import {
  clockTime,
  dayKeyIn,
  formatIn,
  todayIn,
} from '../../../../../function-utils/building-time';

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${spacing.xs};

  ${down('sm')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Stat = styled.button<{ alert?: boolean; $active?: boolean }>`
  -webkit-appearance: none;
  appearance: none;
  display: block;
  width: 100%;
  text-align: left;
  font: inherit;
  cursor: pointer;
  border: 1px solid
    ${({ alert, $active }) =>
      $active ? palette.iris : alert ? palette.warning : palette.line};
  background: ${({ alert, $active }) =>
    $active ? palette.irisTint : alert ? palette.warningSoft : 'transparent'};
  border-radius: ${radius.xs};
  padding: ${spacing.half} ${spacing.xs};
  min-width: 0;
  transition: ${transition.colors};

  b {
    display: block;
    font-size: ${typography.size.lg}px;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    /* The alerting count sits ON the amber wash, so its label is the status
       foreground, not the full-strength hue: amber on amber measured 1.92:1 at
       20 and 24px. The BORDER above keeps palette.warning, which is what that
       token is for. */
    color: ${({ alert, $active }) =>
      $active ? palette.irisDeep : alert ? statusTokens.warning.fg : palette.ink};
  }

  span {
    display: block;
    margin-top: 4px;
    font-size: ${typography.size.xxs}px;
    letter-spacing: ${typography.tracking.caps};
    text-transform: uppercase;
    color: ${({ $active }) => ($active ? palette.irisDeep : palette.slateLight)};
  }

  ${touch} {
    min-height: 64px;
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Row = styled.li`
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr) auto;
  gap: ${spacing.half} ${spacing.xs};
  align-items: baseline;
  padding: ${spacing.half} 4px;
  border-top: 1px solid ${palette.line};

  &:first-of-type {
    border-top: none;
  }

  /* On a phone the third column has nowhere to go. Drop to two columns and let
     the action sit under the entry, which is also the reading order. */
  ${down('sm')} {
    grid-template-columns: 52px minmax(0, 1fr);

    & > :last-child {
      grid-column: 2;
    }
  }
`;

const When = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
  font-variant-numeric: tabular-nums;
  line-height: ${typography.leading.snug};

  > b {
    font-weight: ${typography.weight.regular};
    opacity: 0.85;
    white-space: nowrap;
  }
`;

/**
 * THE STATUS TRIPLES, not a second hand-built copy of them.
 *
 * This table paired each tint with the FULL-STRENGTH semantic hue as its label:
 * amber on amber measured 1.92:1 and coral on coral 2.93:1 — the two worst pairs
 * in the product, on the dashboard's own activity chips. `statusTokens` (theme `status.*`) exists so a
 * tint always carries a foreground that can be read on it, and the identical
 * table in activity-feed.tsx:335 was already built from it. This one had drifted.
 */
const CHIP_TONE = {
  in: { bg: statusTokens.brand.bg, fg: statusTokens.brand.fg },
  out: { bg: statusTokens.brand.bg, fg: statusTokens.brand.fg },
  kind: { bg: statusTokens.neutral.bg, fg: statusTokens.neutral.fg },
  open: { bg: statusTokens.warning.bg, fg: statusTokens.warning.fg },
  alert: { bg: statusTokens.alert.bg, fg: statusTokens.alert.fg },
} as const;

const Chip = styled.span<{ tone?: keyof typeof CHIP_TONE }>`
  display: inline-block;
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  padding: 0 ${spacing.half};
  border-radius: ${radius.xs};
  margin-right: ${spacing.half};
  white-space: nowrap;
  color: ${({ tone }) => CHIP_TONE[tone || 'in'].fg};
  background: ${({ tone }) => CHIP_TONE[tone || 'in'].bg};
`;

const Meta = styled.span`
  display: block;
  margin-top: ${spacing.half};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};

  > span + span::before {
    content: ' · ';
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  flex-wrap: wrap;
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
`;

/** The window's own zone, so a desk day rendered here matches the one the server
 *  cut: a shift belongs to the BUILDING's clock, not the viewer's. */
const timeIn = (iso: string, tz: string): string => clockTime(iso, tz);

const dateIn = (iso: string, tz: string): string =>
  formatIn(iso, tz, { day: 'numeric', month: 'short' });

/** Same-calendar-day in the RENDERING zone, compared as day keys rather than by
 *  arithmetic on instants — which is what keeps it correct across a zone whose
 *  offset is not a whole number of hours. */
const isToday = (iso: string, tz: string): boolean => {
  const day = dayKeyIn(iso, tz);

  return Boolean(day) && day === todayIn(tz);
};

const summarise = (entry: ActivityEntry): string => {
  const who = entry.people.filter(Boolean).join(', ');
  const note = entry.text.find(Boolean) || '';

  return [who, note].filter(Boolean).join(' — ') || entry.kind;
};

export interface ActivityPanelProps {
  state: ChartShellState;
  data?: ActivityFeedResponse;
  filter: DashboardFilter;
  dispatch: (action: FilterAction, opts?: { replace?: boolean }) => void;
  compact?: boolean;
  onSeeAll?: () => void;
  onOpenRecord?: (source: string, id: string | number) => void;
}

export const ActivityPanel: React.FC<ActivityPanelProps> = ({
  state,
  data,
  filter,
  dispatch,
  compact,
  onSeeAll,
  onOpenRecord,
}) => {
  /* The answer's own clock (`tz`, R-T12) — the reader's for an owner or a
     manager. It read `window.tz`, which is right at the desk and wrong here. */
  const clock = useClock({ buildingId: filter.buildingId, response: data });
  const tz = clock.zone;
  const entries = data?.entries || [];
  const shown = compact ? entries.slice(0, 8) : entries;

  /**
   * THE TILE STRIP, AS ONE FOUR-WAY CHOICE.
   *
   * `status` and `direction` are two different query fields, and letting both be
   * on at once produces states nobody pressed for — "still inside AND going out"
   * is empty by definition, and an empty feed the reader did not ask for is the
   * "0 on everything" report all over again. The reducer enforces the exclusion;
   * this is the control that expresses it.
   */
  const toggleStat = (pick: 'open' | 'in' | 'out' | 'all') => {
    if (pick === 'all') {
      dispatch({ type: 'patch', patch: { status: null, direction: null } });

      return;
    }
    if (pick === 'open') {
      dispatch({
        type: 'patch',
        patch: { direction: null, status: filter.status === 'open' ? null : 'open' },
      });

      return;
    }
    dispatch({
      type: 'patch',
      patch: { status: null, direction: filter.direction === pick ? null : pick },
    });
  };

  const renderRow = (entry: ActivityEntry, i: number) => {
    const detail = [
      entry.subKind,
      entry.parcelCount && entry.parcelCount > 1 ? `${entry.parcelCount} items` : '',
      Number.isInteger(entry.headcount) && (entry.headcount as number) > 1
        ? `party of ${entry.headcount}`
        : '',
      entry.priority && entry.priority !== 'Low' ? entry.priority : '',
    ]
      .filter(Boolean)
      .join(' · ');

    /* ONE TAG PER ROW, NOT TWO SAYING THE SAME THING. The tag is
       "<group> <direction>". The KIND is a finer classifier and is worth a chip
       only when it ADDS something: a parcel's kind is literally "Parcel out", so
       rendering both produced "Parcels out  Parcel out". Compared on the WORDS
       rather than on a source allow-list, so a kind that restates its group is
       dropped wherever it appears. */
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z]/g, '');
    const tag = `${entry.groupLabel}${entry.direction ? ` ${entry.direction}` : ''}`;
    const kindAdds =
      !!entry.kind &&
      norm(entry.kind) !== norm(tag) &&
      norm(entry.kind) !== norm(entry.groupLabel) &&
      !norm(tag).startsWith(norm(entry.kind).replace(/s$/, '')) &&
      !norm(entry.kind).startsWith(norm(tag).replace(/s$/, ''));

    /* AN AUTO-CLOSE IS NOT AN OBSERVATION. "out 01:59" on a 09:24 arrival is the
       nightly sweep tidying a record nobody closed, and rendering it as a plain
       departure time claims somebody watched that person leave at two in the
       morning. chk_vl_exit_machine makes exit_source sufficient on its own to
       tell the two apart. */
    const observedExit = entry.exitSource === 'desk';

    return (
      <Row
        key={`${entry.ref.source}:${entry.ref.id}:${entry.direction}:${i}`}
        data-at={entry.at}
        data-building-zone={entry.tz || undefined}
      >
        <When>
          <span data-time="">{timeIn(entry.at, tz)}</span>
          {!isToday(entry.at, tz) && <b>{dateIn(entry.at, tz)}</b>}
        </When>
        <span>
          <Chip tone={entry.direction === 'out' ? 'out' : 'in'}>{tag}</Chip>
          {kindAdds && <Chip tone="kind">{entry.kind}</Chip>}
          {entry.severity === 'incident' && <Chip tone="alert">Incident</Chip>}
          {entry.severity === 'follow_up' && <Chip tone="open">Follow up</Chip>}
          {/* THE SAME STATE, NAMED PER SUBJECT. A parcel is open too, and "Still
              inside" is the wrong words for it. */}
          {entry.open === true && (
            <Chip tone="open">
              {entry.source === 'daily_log'
                ? 'Still open'
                : entry.source === 'package'
                ? 'Not collected'
                : 'Still inside'}
            </Chip>
          )}
          {summarise(entry)}
          <Meta>
            {entry.unit && <span>Unit {entry.unit}</span>}
            {detail && <span>{detail}</span>}
            {entry.departedAt && (
              <span>
                {observedExit
                  ? `out ${timeIn(entry.departedAt, tz)}`
                  : 'closed automatically, nobody recorded them leaving'}
              </span>
            )}
            {entry.direction === 'out' && entry.source !== 'package' && (
              <span>
                {observedExit
                  ? 'recorded at the desk'
                  : 'closed automatically, nobody recorded the end'}
              </span>
            )}
            {/* WHO DID IT, ALWAYS. A name we cannot decrypt renders as 'Unknown',
                never as nothing and never as the raw token. THE ROLE IS A LABEL,
                not the stored slug — it rendered as "(property_manager)", which
                is a database value on a screen. */}
            <span>
              by {entry.actor?.name || 'Unknown'}
              {entry.actor?.name && normalizeUserRole(entry.actor.role)
                ? ` (${roleLabel(entry.actor.role, { short: true })})`
                : ''}
            </span>
          </Meta>
        </span>
        <span>
          {/* WP-36 / MR-14 — A FOURTH RENDER SITE THE PLAN'S INVENTORY MISSED.
              It lists records-tab (twice), activity-feed and activity-tab; this
              panel is what the dashboard's Activity tab actually renders, and it
              had the same anonymous "Open" as the other three. Found by reading
              the LIVE accessibility tree rather than the plan: the probe counted
              25 controls still named exactly "Open" here after the three listed
              sites were fixed.

              Same rule as the feed: the visible word stays short, the name gains
              the row, and both are built from `summarise` and `timeIn` — the
              same calls the visible line uses, so they cannot drift. */}
          {onOpenRecord ? (
            <PanelToggle
              type="button"
              aria-label={openRecordLabel(sourceNoun(entry.source), [
                summarise(entry),
                entry.unit ? `unit ${entry.unit}` : '',
                entry.at ? timeIn(entry.at, tz) : '',
              ])}
              onClick={() => onOpenRecord(entry.ref.source, entry.ref.id)}
            >
              Open
            </PanelToggle>
          ) : null}
        </span>
      </Row>
    );
  };

  return (
    <PanelShell
      id="activity"
      title={compact ? 'Recent activity' : 'All activity'}
      wide
      state={state}
      caveat="One timeline, newest first. Every row carries a tag, so the sequence of a shift stays readable without splitting it into four lists."
      emptyReason={
        data && !entries.length
          ? {
              title: 'Nothing has been logged in this window yet',
              text: 'A quiet log and a quiet building are different things. Widen the range, or clear a filter above.',
            }
          : null
      }
      controls={
        compact && onSeeAll ? (
          <PanelToggle type="button" onClick={onSeeAll} data-testid="see-all-activity">
            See everything
          </PanelToggle>
        ) : undefined
      }
    >
      {data ? (
        <>
          <Stats role="group" aria-label="Narrow the feed">
            <Stat
              type="button"
              alert={data.openCount > 0}
              $active={filter.status === 'open'}
              aria-pressed={filter.status === 'open'}
              data-testid="stat-open"
              onClick={() => toggleStat('open')}
            >
              <b>{data.openCount}</b>
              <span>Still inside</span>
            </Stat>
            <Stat
              type="button"
              $active={filter.direction === 'in'}
              aria-pressed={filter.direction === 'in'}
              data-testid="stat-in"
              onClick={() => toggleStat('in')}
            >
              <b>{data.inCount}</b>
              <span>In</span>
            </Stat>
            <Stat
              type="button"
              $active={filter.direction === 'out'}
              aria-pressed={filter.direction === 'out'}
              data-testid="stat-out"
              onClick={() => toggleStat('out')}
            >
              <b>{data.outCount}</b>
              <span>Out</span>
            </Stat>
            {/* NOT A TOGGLE. "Entries" is the whole window, so it clears the two
                narrowings the other three apply — lit when neither is on, which
                makes the strip read as one four-way choice. */}
            <Stat
              type="button"
              $active={!filter.status && !filter.direction}
              aria-pressed={!filter.status && !filter.direction}
              data-testid="stat-all"
              onClick={() => toggleStat('all')}
            >
              {/* COUNTED WITH THIS TILE'S OWN AXES LIFTED, like the three
                  beside it. `total` answers to every filter including direction
                  and status, so while "In" was pressed this tile showed the In
                  count and the strip read as though the window had shrunk.
                  `moveTotal` is the server's figure for "the window, ignoring
                  direction and status" — exactly what pressing this clears.
                  The `?? total` keeps an older server truthful rather than 0. */}
              <b>
                {data.totalCapped
                  ? `${data.moveTotal ?? data.total}+`
                  : data.moveTotal ?? data.total}
              </b>
              <span>Entries</span>
            </Stat>
          </Stats>

          <List>{shown.map(renderRow)}</List>

          <Footer>
            {/* HOW FAR THROUGH, AND WHETHER THERE IS MORE. `totalCapped` means
                the server stopped counting at its scan ceiling, so the total is a
                FLOOR and is rendered as "118+" rather than as a confident
                figure. */}
            <span>
              {data.hasMore
                ? `Showing ${shown.length} of ${data.totalCapped ? `${data.total}+` : data.total}`
                : `All ${data.total}${data.totalCapped ? '+' : ''} shown`}
            </span>
            {/* WHICH CLOCK THE TIMES ARE ON, SAID OUT LOUD. */}
            <span>
              · <ClockNote clock={clock} inline />
            </span>
            {!compact && data.hasMore ? (
              <PanelToggle
                type="button"
                data-testid="activity-more"
                onClick={() => dispatch({ type: 'setPage', page: filter.page + 1 })}
              >
                Show more
              </PanelToggle>
            ) : null}
          </Footer>
        </>
      ) : null}
    </PanelShell>
  );
};
